import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Paper,
  makeStyles
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  FileCopy,
  VpnKey
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 500
  },
  infoBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === "light" ? "#fff9c4" : "#f57f17",
    borderRadius: theme.shape.borderRadius
  },
  tokenBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    // Fundo amarelo, igual ao webhookIncomingBox
    backgroundColor: theme.palette.mode === "light" ? "#fff9c4" : "#f57f17",
    borderRadius: theme.shape.borderRadius
  },
  webhookIncomingBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === "light" ? "#fff9c4" : "#f57f17",
    borderRadius: theme.shape.borderRadius
  }
}));

const WebhookConnectionModal = ({ open, onClose, connection, onSave }) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [tokenEnabled, setTokenEnabled] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (connection) {
      setName(connection.name || "");
      setWebhookUrl(connection.webhookUrl || "");
      setTokenEnabled(connection.tokenEnabled || false);
    } else {
      setName("");
      setWebhookUrl("");
      setTokenEnabled(false);
    }
    setShowToken(false);
  }, [connection, open]);

  const handleSave = async () => {
    if (!name || !webhookUrl) {
      toast.error("Nome e URL do webhook são obrigatórios");
      return;
    }

    setLoading(true);
    try {
      await onSave({
        name,
        webhookUrl,
        tokenEnabled
      });
      onClose();
    } catch (err) {
      // Erro já tratado no hook
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (connection?.webhookIncomingToken) {
      navigator.clipboard.writeText(connection.webhookIncomingToken);
      toast.success("Token copiado para a área de transferência!");
    }
  };

  const handleCopyUrl = () => {
    if (connection?.webhookIncomingUrl) {
      navigator.clipboard.writeText(connection.webhookIncomingUrl);
      toast.success("URL copiada para a área de transferência!");
    }
  };

  // Helper genérico para montar curl respeitando token
  const buildCurlForPayload = (payload, useRealToken = false) => {
    // Sempre gerar um comando em **uma linha**, compatível com import do Postman
    const url = connection?.webhookIncomingUrl || "{url_fornecida}";
    const hasToken = connection?.tokenEnabled && connection?.webhookIncomingToken;
    const tokenValue =
      useRealToken && hasToken ? connection.webhookIncomingToken : "{token}";

    const authHeader = hasToken
      ? ` -H "Authorization: Bearer ${tokenValue}"`
      : "";

    // IMPORTANTE: tudo em uma linha, sem barras invertidas de quebra
    return `curl -X POST "${url}"${authHeader} -H "Content-Type: application/json" -d '${payload}'`;
  };

  // Helper para copiar com fallback
  const safeCopy = (text) => {
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        navigator.clipboard.writeText(text);
        toast.success("Comando curl copiado para a área de transferência!");
      } else {
        console.warn("API Clipboard não suportada. Comando:", text);
        toast.info("Seu navegador não permite copiar automaticamente. Copie o comando manualmente.");
      }
    } catch (err) {
      console.error("Erro ao copiar comando curl:", err);
      toast.error("Erro ao copiar comando curl. Copie manualmente o comando exibido.");
    }
  };

  // Payloads de exemplo (JSON) - usados para montar os curls
  const payload1 = `{
  "contact": {
    "number": "5511999999999",
    "name": "João Silva",
    "email": "joao@example.com"
  },
  "message": {
    "text": "Olá, preciso de ajuda!",
    "wid": "msg-123456"
  }
}`;

  const payload2 = `{
  "contact": {
    "number": "5511999999999",
    "name": "João Silva"
  },
  "message": {
    "mediaUrl": "https://exemplo.com/audio.mp3",
    "mediaType": "audio",
    "wid": "msg-123457"
  }
}`;

  const payload3 = `{
  "contact": {
    "number": "5511999999",
    "name": "João Silva"
  },
  "message": {
    "mediaUrl": "https://exemplo.com/imagem.jpg",
    "mediaType": "image",
    "text": "Legenda da imagem (opcional)",
    "wid": "msg-123458"
  }
}`;

  const payload4 = `{
  "contact": {
    "number": "5511999999999",
    "name": "João Silva"
  },
  "message": {
    "mediaBase64": "data:image/png;base64,iVBORw0KGgoAAAANS...",
    "mediaType": "image",
    "text": "Legenda (opcional)",
    "wid": "msg-123459"
  }
}`;

  const payload5 = `{
  "contact": {
    "number": "5511999999999",
    "name": "João Silva"
  },
  "message": {
    "text": "Nota interna para o atendente",
    "wid": "msg-123460"
  },
  "isPrivate": true
}`;

  const payload6 = `{
  "contact": {
    "number": "5511999999999",
    "name": "João Silva"
  },
  "message": {
    "text": "Mensagem do cliente",
    "wid": "msg-123461"
  },
  "ticket": {
    "userId": 1,
    "queueId": 2,
    "tagId": 3,
    "status": "open"
  }
}`;

  // curls e handlers por exemplo
  const curl1 = buildCurlForPayload(payload1, false);
  const curl2 = buildCurlForPayload(payload2, false);
  const curl3 = buildCurlForPayload(payload3, false);
  const curl4 = buildCurlForPayload(payload4, false);
  const curl5 = buildCurlForPayload(payload5, false);
  const curl6 = buildCurlForPayload(payload6, false);

  const handleCopyCurl1 = () => safeCopy(buildCurlForPayload(payload1, true));
  const handleCopyCurl2 = () => safeCopy(buildCurlForPayload(payload2, true));
  const handleCopyCurl3 = () => safeCopy(buildCurlForPayload(payload3, true));
  const handleCopyCurl4 = () => safeCopy(buildCurlForPayload(payload4, true));
  const handleCopyCurl5 = () => safeCopy(buildCurlForPayload(payload5, true));
  const handleCopyCurl6 = () => safeCopy(buildCurlForPayload(payload6, true));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {connection ? "Editar Conexão Webhook" : "Nova Conexão Webhook"}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <TextField
          label="Nome"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          required
        />

        <TextField
          label="URL do Webhook de Saída"
          fullWidth
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          margin="normal"
          required
          helperText="URL para onde o sistema enviará mensagens do usuário para o contato"
        />

        <FormControlLabel
          control={
            <Switch
              checked={tokenEnabled}
              onChange={(e) => setTokenEnabled(e.target.checked)}
              color="primary"
            />
          }
          label="Habilitar autenticação por token no webhook de entrada"
          style={{ marginTop: 16 }}
        />

        {connection && connection.webhookIncomingUrl && (
          <>
            <Paper className={classes.webhookIncomingBox} elevation={0}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Webhook de Entrada (fornecido pelo sistema):</strong>
              </Typography>
              <TextField
                fullWidth
                value={connection.webhookIncomingUrl}
                margin="normal"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopyUrl} size="small">
                        <FileCopy />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Paper>

            {connection.tokenEnabled && connection.webhookIncomingToken && (
              <Paper className={classes.tokenBox} elevation={0}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Token de Autenticação:</strong>
                </Typography>
                <TextField
                  fullWidth
                  type={showToken ? "text" : "password"}
                  value={connection.webhookIncomingToken}
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowToken(!showToken)}
                          size="small"
                        >
                          {showToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton onClick={handleCopyToken} size="small">
                          <FileCopy />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Paper>
            )}
          </>
        )}

        <Paper className={classes.infoBox} elevation={0}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Como usar:</strong>
          </Typography>

          <Typography
            variant="body2"
            style={{ marginTop: 16, fontSize: "0.875rem", fontWeight: "bold" }}
          >
            1. Enviando mensagem de texto (curl pronto para Postman):
          </Typography>
          <Typography variant="body2" component="div" style={{ marginTop: 4 }}>
            <code
              style={{
                display: "block",
                padding: 8,
                backgroundColor: "rgba(0,0,0,0.05)",
                borderRadius: 4,
                fontSize: "0.75rem",
                overflowX: "auto"
              }}
            >
              {curl1}
            </code>
          </Typography>

          <Typography
            variant="body2"
            style={{ marginTop: 16, fontSize: "0.875rem", fontWeight: "bold" }}
          >
            2. Enviando áudio:
          </Typography>
          <Typography
            variant="body2"
            component="div"
            style={{ marginTop: 4 }}
          >
            <code
              style={{
                display: "block",
                padding: 8,
                backgroundColor: "rgba(0,0,0,0.05)",
                borderRadius: 4,
                fontSize: "0.75rem",
                overflowX: "auto"
              }}
            >
              {curl2}
            </code>
          </Typography>

          <Typography
            variant="body2"
            style={{ marginTop: 12, fontSize: "0.875rem", fontWeight: "bold" }}
          >
            3. Enviando mídia (imagem/vídeo):
          </Typography>
          <Typography variant="body2" component="div" style={{ marginTop: 4 }}>
            <code
              style={{
                display: "block",
                padding: 8,
                backgroundColor: "rgba(0,0,0,0.05)",
                borderRadius: 4,
                fontSize: "0.75rem",
                overflowX: "auto"
              }}
            >
              {curl3}
            </code>
          </Typography>

          <Typography
            variant="body2"
            style={{ marginTop: 12, fontSize: "0.875rem", fontWeight: "bold" }}
          >
            4. Enviando mídia via Base64 (tipo detectado automaticamente):
          </Typography>
          <Typography variant="body2" component="div" style={{ marginTop: 4 }}>
            <code
              style={{
                display: "block",
                padding: 8,
                backgroundColor: "rgba(0,0,0,0.05)",
                borderRadius: 4,
                fontSize: "0.75rem",
                overflowX: "auto"
              }}
            >
              {curl4}
            </code>
          </Typography>

          <Typography
            variant="body2"
            style={{ marginTop: 12, fontSize: "0.875rem", fontWeight: "bold" }}
          >
            5. Mensagem privada (só para atendente, não aparece para o usuário):
          </Typography>
          <Typography variant="body2" component="div" style={{ marginTop: 4 }}>
            <code
              style={{
                display: "block",
                padding: 8,
                backgroundColor: "rgba(0,0,0,0.05)",
                borderRadius: 4,
                fontSize: "0.75rem",
                overflowX: "auto"
              }}
            >
              {curl5}
            </code>
          </Typography>

          <Typography
            variant="body2"
            style={{ marginTop: 12, fontSize: "0.875rem", fontWeight: "bold" }}
          >
            6. Criar/atualizar ticket com userId, queueId, tagId e status:
          </Typography>
          <Typography variant="body2" component="div" style={{ marginTop: 4 }}>
            <code
              style={{
                display: "block",
                padding: 8,
                backgroundColor: "rgba(0,0,0,0.05)",
                borderRadius: 4,
                fontSize: "0.75rem",
                overflowX: "auto"
              }}
            >
              {curl6}
            </code>
          </Typography>

          <Typography
            variant="body2"
            style={{ marginTop: 12, fontSize: "0.875rem" }}
          >
            <strong>Nota:</strong> O sistema baixará automaticamente a mídia da URL
            fornecida ou processará base64 e salvará localmente. O tipo de mídia é
            detectado automaticamente.
          </Typography>
          <Typography
            variant="body2"
            style={{ marginTop: 8, fontSize: "0.875rem" }}
          >
            Envie mensagens para este webhook de entrada para criar tickets
            automaticamente. O contato não precisa ter WhatsApp válido.
          </Typography>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || !name || !webhookUrl}
        >
          {loading ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebhookConnectionModal;
